import { useEffect, useRef, useState } from "react";
import { stats } from "@/mocks/home";

function useCountUp(target: number, start: boolean, duration = 1600) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const startTime = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);
  return value;
}

function StatItem({ icon, value, suffix, label, start }: { icon: string; value: number; suffix: string; label: string; start: boolean }) {
  const count = useCountUp(value, start);
  return (
    <div className="flex flex-col items-center text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-primary-300 md:h-16 md:w-16">
        <i className={`${icon} text-3xl md:text-4xl`} />
      </span>
      <span className="mt-4 font-heading text-3xl font-extrabold text-background-50 md:text-4xl">
        {count}
        {suffix}
      </span>
      <span className="mt-1 text-xs font-medium uppercase tracking-wide text-background-200 md:text-sm">{label}</span>
    </div>
  );
}

export default function StatsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [start, setStart] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setStart(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative overflow-hidden bg-foreground-950 py-14 md:py-16">
      <div className="absolute -left-16 top-0 h-64 w-64 rounded-full bg-primary-500/20 blur-3xl" />
      <div className="absolute -right-16 bottom-0 h-64 w-64 rounded-full bg-accent-500/20 blur-3xl" />
      <div className="relative mx-auto max-w-screen-2xl px-4 md:px-6">
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-5">
          {stats.map((s) => (
            <StatItem key={s.label} icon={s.icon} value={s.value} suffix={s.suffix} label={s.label} start={start} />
          ))}
        </div>
      </div>
    </section>
  );
}